(function () {
  if (window.__colmiDynamicVariantOptionsLoaded) {
    return;
  }

  window.__colmiDynamicVariantOptionsLoaded = true;

  const pickerSelector = 'variant-picker[data-dynamic-variants]';
  const optimizedProductPath = '/products/colmi-ring-2';
  const optimisticRollbackDelay = 8000;
  const patchedPickers = new WeakSet();
  const optimisticStates = new WeakMap();
  const warmedVariantIds = new WeakMap();
  let pendingPatch = false;
  let observer;
  let variantChangeUnsubscribe;

  function getProductInfo(picker) {
    return picker.closest('product-info');
  }

  function isOptimizedProductInfo(productInfo) {
    if (!productInfo) {
      return false;
    }

    const productUrl = productInfo.getAttribute('data-product-url');
    if (!productUrl) {
      return false;
    }

    return new URL(productUrl, window.location.origin).pathname.replace(/\/$/, '').endsWith(optimizedProductPath);
  }

  function getVariantData(picker) {
    if (picker.__colmiVariantData) {
      return picker.__colmiVariantData;
    }

    const source = picker.querySelector('[data-variants]');
    picker.__colmiVariantData = source ? JSON.parse(source.textContent) : [];
    return picker.__colmiVariantData;
  }

  function getGroups(picker) {
    return [...picker.querySelectorAll('fieldset, .variant-wrapper')];
  }

  function getCurrentOptions(picker, target) {
    return getGroups(picker).map((group) => {
      const select = group.querySelector('select');
      if (select) {
        return target?.tagName === 'OPTION' && select.contains(target) ? target.value : select.value;
      }

      return [...group.querySelectorAll('input')].find((radio) => radio === target || radio.checked)?.value;
    });
  }

  function findVariantByOptions(variants, options, requireAvailability) {
    return variants.find((variant) => {
      const matches = options.every((value, index) => variant[`option${index + 1}`] === value);
      return requireAvailability ? matches && variant.available : matches;
    });
  }

  function findClosestVariant(variants, options, target, requireAvailability) {
    if (!target) {
      return null;
    }

    const lastIndex = parseInt(target.getAttribute('data-option-index'), 10);
    const matches = variants.filter((variant) =>
      (!requireAvailability || variant.available) &&
      options.slice(0, lastIndex).every((value, index) => variant[`option${index + 1}`] === value)
    );

    return matches.reduce((best, variant) => {
      if (!best) {
        return variant;
      }

      const bestScore = options.reduce((sum, value, index) =>
        best[`option${index + 1}`] === value ? sum + (3 - index) : sum, 0);
      const currentScore = options.reduce((sum, value, index) =>
        variant[`option${index + 1}`] === value ? sum + (3 - index) : sum, 0);

      return currentScore > bestScore ? variant : best;
    }, null);
  }

  function findCurrentVariant(variants, options, target) {
    return findVariantByOptions(variants, options, true) ||
      findClosestVariant(variants, options, target, true) ||
      findVariantByOptions(variants, options, false) ||
      findClosestVariant(variants, options, target, false) ||
      null;
  }

  function getControls(group) {
    return [...group.querySelectorAll('input[type="radio"], option')];
  }

  function getControlForValue(group, value) {
    return getControls(group).find((control) => control.value === value);
  }

  function getVariantOptionValueIds(picker, variant) {
    if (!variant) {
      return [];
    }

    return getGroups(picker).map((group, index) =>
      getControlForValue(group, variant[`option${index + 1}`])?.getAttribute('data-option-value-id')
    ).filter(Boolean);
  }

  function findVariantFromOptionValueIds(picker, optionValueIds, target) {
    const groups = getGroups(picker);
    const options = getCurrentOptions(picker, target);

    optionValueIds.forEach((optionValueId) => {
      groups.some((group, index) => {
        const control = getControls(group).find((item) => item.getAttribute('data-option-value-id') === optionValueId);
        if (!control) {
          return false;
        }

        options[index] = control.value;
        return true;
      });
    });

    return findCurrentVariant(getVariantData(picker), options, target);
  }

  function canWarmControl(control) {
    return !control.disabled &&
      !control.classList.contains('unavailable-combination') &&
      !control.closest('li')?.hidden;
  }

  // Normalize Concept's hover preload to a real variant so its native cache is reusable on click.
  function patchNativePreload() {
    const ProductInfo = customElements.get('product-info');
    const prototype = ProductInfo?.prototype;
    if (!prototype || prototype.__colmiPreloadPatched) {
      return;
    }

    const nativePreloadOptionValues = prototype.preloadOptionValues;
    if (typeof nativePreloadOptionValues !== 'function') {
      return;
    }

    prototype.__colmiPreloadPatched = true;
    prototype.preloadOptionValues = function (optionValueIds, target) {
      const picker = target?.closest?.(pickerSelector) || this.querySelector(pickerSelector);
      if (!picker || !isOptimizedProductInfo(this)) {
        return nativePreloadOptionValues.call(this, optionValueIds, target);
      }

      if (target && !canWarmControl(target)) {
        return;
      }

      const variant = findVariantFromOptionValueIds(picker, optionValueIds, target);
      const normalizedOptionValueIds = getVariantOptionValueIds(picker, variant);
      if (!variant || normalizedOptionValueIds.length !== getGroups(picker).length) {
        return;
      }

      return nativePreloadOptionValues.call(this, normalizedOptionValueIds, target);
    };
  }

  function warmPrimaryOptions(picker) {
    const productInfo = getProductInfo(picker);
    const variants = getVariantData(picker);
    const groups = getGroups(picker);
    const primaryGroup = groups[0];
    if (!isOptimizedProductInfo(productInfo) || !variants.length || !primaryGroup) {
      return;
    }

    let warmedIds = warmedVariantIds.get(productInfo);
    if (!warmedIds) {
      warmedIds = new Set();
      warmedVariantIds.set(productInfo, warmedIds);
    }

    getControls(primaryGroup)
      .filter((control) => !control.checked && !control.selected && canWarmControl(control))
      .slice(0, 2)
      .forEach((control) => {
        const variant = findCurrentVariant(variants, getCurrentOptions(picker, control), control);
        const optionValueIds = getVariantOptionValueIds(picker, variant);
        if (!variant || warmedIds.has(variant.id) || optionValueIds.length !== groups.length) {
          return;
        }

        warmedIds.add(variant.id);
        productInfo.preloadOptionValues?.(optionValueIds, control);
      });
  }

  // Replace Concept's broad option warmup with at most two real alternatives from the first option group.
  function scheduleTargetedWarmup(picker) {
    if (picker.__colmiTargetedWarmupScheduled) {
      return;
    }

    picker.__colmiTargetedWarmupScheduled = true;
    const connection = navigator.connection;
    if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) {
      return;
    }

    const warm = () => {
      if (picker.isConnected) {
        warmPrimaryOptions(picker);
      }
    };
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(warm, { timeout: 1500 });
      } else {
        setTimeout(warm, 400);
      }
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }
  }

  function getProductIdentifiers(productInfo) {
    return {
      sectionId: productInfo.hasAttribute('data-original-section-id')
        ? productInfo.getAttribute('data-original-section-id')
        : productInfo.getAttribute('data-section-id'),
      productId: productInfo.getAttribute('data-product-id')
    };
  }

  function captureAttributes(element) {
    return [...element.attributes].map((attribute) => [attribute.name, attribute.value]);
  }

  function restoreAttributes(element, attributes) {
    [...element.attributes].forEach((attribute) => element.removeAttribute(attribute.name));
    attributes.forEach(([name, value]) => element.setAttribute(name, value));
  }

  function captureProductSnapshot(productInfo) {
    const { sectionId, productId } = getProductIdentifiers(productInfo);
    const htmlElements = ['Price', 'BuyButtonPrice', 'StickyPrice']
      .map((prefix) => document.getElementById(`${prefix}-${sectionId}-${productId}`))
      .filter(Boolean)
      .map((element) => ({ element, html: element.innerHTML }));
    const gallery = productInfo.closest('.product')?.querySelector('media-gallery');
    const mediaLists = gallery
      ? [...gallery.querySelectorAll('.product__media-list, .product__thumbnails-list')]
        .map((element) => ({ element, children: [...element.children] }))
      : [];
    const previewMedia = gallery?.querySelector('.product__preview [data-media-id]');

    return {
      htmlElements,
      gallery,
      mediaLists,
      preview: previewMedia
        ? {
            element: previewMedia,
            attributes: captureAttributes(previewMedia),
            html: previewMedia.innerHTML
          }
        : null
    };
  }

  function restoreProductSnapshot(snapshot) {
    snapshot.htmlElements.forEach(({ element, html }) => {
      if (element.isConnected) {
        element.innerHTML = html;
      }
    });
    snapshot.mediaLists.forEach(({ element, children }) => {
      if (element.isConnected) {
        element.replaceChildren(...children);
      }
    });
    if (snapshot.preview?.element.isConnected) {
      restoreAttributes(snapshot.preview.element, snapshot.preview.attributes);
      snapshot.preview.element.innerHTML = snapshot.preview.html;
    }

    snapshot.gallery?.sliderGallery?.reset?.();
    snapshot.gallery?.countMediaGallery?.();
  }

  function getMoneyFormat() {
    const settings = window.theme?.settings;
    return settings?.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings?.moneyFormat;
  }

  function updateOptimisticPrice(productInfo, variant) {
    if (typeof variant?.price !== 'number' || !window.theme?.Currency?.formatMoney) {
      return;
    }

    const formattedPrice = window.theme.Currency.formatMoney(variant.price, getMoneyFormat());
    const { sectionId, productId } = getProductIdentifiers(productInfo);
    ['Price', 'StickyPrice'].forEach((prefix) => {
      const container = document.getElementById(`${prefix}-${sectionId}-${productId}`);
      container?.querySelectorAll('.price__regular').forEach((element) => {
        element.textContent = formattedPrice;
      });
    });

    const buyButtonPrice = document.getElementById(`BuyButtonPrice-${sectionId}-${productId}`);
    buyButtonPrice?.querySelectorAll('product-buy-price').forEach((element) => {
      element.setAttribute('data-price', variant.price);
      element.textContent = formattedPrice;
    });
  }

  function getVariantMedia(variant) {
    const media = variant?.featured_media || variant?.featured_image;
    if (!media) {
      return null;
    }

    return {
      id: String(media.id),
      alt: media.alt || media.preview_image?.alt || '',
      image: media.preview_image || media
    };
  }

  function copyImageAttributes(source, destination, fallbackAlt) {
    ['src', 'srcset', 'sizes', 'width', 'height'].forEach((name) => {
      const value = source.getAttribute(name);
      if (value) {
        destination.setAttribute(name, value);
      } else {
        destination.removeAttribute(name);
      }
    });
    destination.alt = source.alt || fallbackAlt;
    destination.loading = 'eager';
    destination.fetchPriority = 'high';
  }

  function applyOptimisticMedia(productInfo, variant) {
    const media = getVariantMedia(variant);
    const gallery = productInfo.closest('.product')?.querySelector('media-gallery');
    if (!media || !gallery) {
      return;
    }

    const mediaLists = [...gallery.querySelectorAll('.product__media-list')];
    const targetMedia = mediaLists
      .map((list) => [...list.children].find((item) => item.getAttribute('data-media-id') === media.id))
      .find(Boolean);
    const targetImage = targetMedia?.querySelector('img');
    if (!targetMedia || !targetImage) {
      return;
    }

    mediaLists.forEach((list) => {
      const item = [...list.children].find((child) => child.getAttribute('data-media-id') === media.id);
      if (item) {
        item.removeAttribute('hidden');
        if (productInfo.closest('.product')?.classList.contains('product--scroll')) {
          item.classList.add('xl:hidden');
        }
        list.prepend(item);
      }
    });

    gallery.querySelectorAll('.product__thumbnails-list').forEach((list) => {
      const item = [...list.children].find((child) => child.getAttribute('data-media-id') === media.id);
      if (item) {
        item.removeAttribute('hidden');
        list.prepend(item);
      }
    });

    const previewMedia = gallery.querySelector('.product__preview [data-media-id]');
    const previewImage = previewMedia?.querySelector('img');
    if (previewMedia && previewImage) {
      previewMedia.setAttribute('data-media-id', media.id);
      previewMedia.setAttribute('data-media-type', 'image');
      copyImageAttributes(targetImage, previewImage, media.alt);
    }

    gallery.sliderGallery?.reset?.();
    gallery.sliderGallery?.scrollTo?.({ left: 0, top: 0, behavior: 'instant' });
    gallery.countMediaGallery?.();
  }

  function queueOptimisticMedia(productInfo, variant, state, version) {
    const media = getVariantMedia(variant);
    const gallery = productInfo.closest('.product')?.querySelector('media-gallery');
    const targetImage = media
      ? gallery?.querySelector(`.product__media-list [data-media-id="${media.id}"] img`)
      : null;
    if (!media || !targetImage) {
      return;
    }

    const apply = () => {
      const activeState = optimisticStates.get(productInfo);
      if (activeState === state && activeState.version === version) {
        applyOptimisticMedia(productInfo, variant);
      }
    };
    if (targetImage.complete && targetImage.naturalWidth > 0) {
      apply();
      return;
    }

    const loader = new Image();
    const ready = new Promise((resolve) => {
      loader.onload = () => resolve(true);
      loader.onerror = () => resolve(false);
    });
    loader.fetchPriority = 'high';
    loader.sizes = targetImage.getAttribute('sizes') || '';
    loader.srcset = targetImage.getAttribute('srcset') || '';
    loader.src = targetImage.getAttribute('src') || media.image.src || '';
    Promise.race([ready, new Promise((resolve) => setTimeout(() => resolve(false), 1200))]).then((loaded) => {
      if (loaded) {
        apply();
      }
    });
  }

  function rollbackOptimisticState(productInfo, state) {
    if (optimisticStates.get(productInfo) !== state) {
      return;
    }

    restoreProductSnapshot(state.snapshot);
    optimisticStates.delete(productInfo);
  }

  function applyOptimisticVariant(picker, variant) {
    const productInfo = getProductInfo(picker);
    if (!variant || !isOptimizedProductInfo(productInfo)) {
      return;
    }

    let state = optimisticStates.get(productInfo);
    if (!state) {
      state = {
        snapshot: captureProductSnapshot(productInfo),
        version: 0,
        variantId: null,
        timeout: null
      };
      optimisticStates.set(productInfo, state);
    }

    state.version += 1;
    state.variantId = String(variant.id);
    clearTimeout(state.timeout);
    // Price and media move first; the native section response remains authoritative for purchasing state.
    updateOptimisticPrice(productInfo, variant);
    queueOptimisticMedia(productInfo, variant, state, state.version);
    state.timeout = setTimeout(() => rollbackOptimisticState(productInfo, state), optimisticRollbackDelay);
  }

  function settleOptimisticState(event) {
    const data = event?.data;
    if (!data?.sectionId || !data?.productId || !data?.variant) {
      return;
    }

    const productInfo = document.getElementById(`ProductInfo-${data.sectionId}-${data.productId}`);
    const state = optimisticStates.get(productInfo);
    if (!state || state.variantId !== String(data.variant.id)) {
      return;
    }

    clearTimeout(state.timeout);
    optimisticStates.delete(productInfo);
  }

  function setInputState(input, exists, isAvailable, isCurrent) {
    if (input.tagName === 'INPUT') {
      input.classList.toggle('unavailable-combination', !exists);
      input.classList.toggle('disabled', exists && !isAvailable);
      input.disabled = !exists;
      input.closest('li')?.toggleAttribute('hidden', !exists);
      input.checked = exists && isCurrent;
      return;
    }

    input.classList.toggle('unavailable-combination', !exists);
    input.hidden = !exists;
    input.disabled = !exists;
    input.selected = exists && isCurrent;
    input.toggleAttribute('selected', exists && isCurrent);
  }

  function updatePicker(picker, target) {
    const variants = getVariantData(picker);
    if (!variants.length) {
      return null;
    }

    const groups = getGroups(picker);
    const options = getCurrentOptions(picker, target);
    const currentVariant = findCurrentVariant(variants, options, target);
    const resolvedOptions = groups.map((group, index) =>
      currentVariant?.[`option${index + 1}`] || options[index]
    );

    groups.forEach((group, index) => {
      const previousOptions = resolvedOptions.slice(0, index);
      const matchingVariants = variants.filter((variant) =>
        previousOptions.every((value, optionIndex) => variant[`option${optionIndex + 1}`] === value)
      );
      const existingValues = matchingVariants.map((variant) => variant[`option${index + 1}`]);
      const availableValues = matchingVariants
        .filter((variant) => variant.available)
        .map((variant) => variant[`option${index + 1}`]);

      [...group.querySelectorAll('input[type="radio"], option')].forEach((input) => {
        const optionIndex = input.getAttribute('data-option-index');
        const value = input.getAttribute('value');
        const exists = existingValues.includes(value);
        const isAvailable = availableValues.includes(value);
        const isCurrent = currentVariant?.[`option${optionIndex}`] === value;

        setInputState(input, exists, isAvailable, isCurrent);
      });
    });

    return currentVariant;
  }

  function patchPicker(picker) {
    if (patchedPickers.has(picker)) {
      updatePicker(picker);
      return;
    }

    patchedPickers.add(picker);
    const optimizePerformance = isOptimizedProductInfo(getProductInfo(picker));
    if (optimizePerformance) {
      picker.optionWarmupDone = true;
    }
    picker.addEventListener('change', (event) => {
      const variant = updatePicker(picker, event.target);
      applyOptimisticVariant(picker, variant);
      schedulePatchAllPickers();
    }, true);
    updatePicker(picker);
    if (optimizePerformance) {
      scheduleTargetedWarmup(picker);
    }
  }

  function patchAllPickers() {
    document.querySelectorAll(pickerSelector).forEach(patchPicker);
  }

  function schedulePatchAllPickers() {
    if (pendingPatch) {
      return;
    }

    pendingPatch = true;
    requestAnimationFrame(() => {
      pendingPatch = false;
      patchAllPickers();
    });
  }

  function subscribeVariantChanges() {
    if (variantChangeUnsubscribe || !window.theme?.pubsub?.PUB_SUB_EVENTS?.variantChange) {
      return;
    }

    variantChangeUnsubscribe = window.theme.pubsub.subscribe(
      window.theme.pubsub.PUB_SUB_EVENTS.variantChange,
      (event) => {
        settleOptimisticState(event);
        schedulePatchAllPickers();
      }
    );
  }

  function observePickerReplacement() {
    if (observer || !document.body) {
      return;
    }

    observer = new MutationObserver((mutations) => {
      const hasPickerReplacement = mutations.some((mutation) =>
        [...mutation.addedNodes].some((node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.(pickerSelector) || node.querySelector?.(pickerSelector))
        )
      );

      if (hasPickerReplacement) {
        schedulePatchAllPickers();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    patchNativePreload();
    patchAllPickers();
    subscribeVariantChanges();
    observePickerReplacement();
  }

  if (customElements.get('variant-picker')) {
    init();
  } else {
    customElements.whenDefined('variant-picker').then(init);
  }

  document.addEventListener('shopify:section:load', schedulePatchAllPickers);
  document.addEventListener('page:loaded', schedulePatchAllPickers);
  document.addEventListener('DOMContentLoaded', init);
})();